import React from "react";
import { useNavigate } from "react-router-dom";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import DeclrAttributeMappingController from "./DeclrAttributeMappingController";

/**
 * 🎯 ATTRIBUTE MAPPING CONFIGURATION (Group ESG admin screen)
 * Map a local Attribute Definition to a group taxonomy concept: unit
 * conversion factor + a fixed aggregation method (SUM/AVERAGE/LATEST in
 * v1). This is the rule the roll-up engine (GroupDeclaration_Calculate)
 * applies to every company's Local Declaration values.
 */
export default function DeclrAttributeMappingView() {
  const navigate = useNavigate();
  const siteKey = process.env.REACT_APP_SITE_KEY;

  const {
    definitions,
    headers,
    selectedHeaderId,
    setSelectedHeaderId,
    concepts,
    aggregationMethods,
    requiresWeight,
    mappings,
    loading,
    saving,
    error,
    form,
    handleFieldChange,
    handleSave,
    handleRemove,
  } = DeclrAttributeMappingController(siteKey);

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <div className="row mb-2">
              <div className="col">
                <h2 className="h4 fw-bold mb-1">Attribute Mapping</h2>
                <p className="text-muted small mb-0">
                  map local attributes to group taxonomy concepts, with unit conversion and roll-up method
                </p>
              </div>
            </div>

            {error && <div className="alert alert-danger extra-small">{error}</div>}

            <div className="row g-3">
              {/* CREATE MAPPING */}
              <div className="col-md-5">
                <div className="card shadow-sm border-0 bg-white p-3 h-100">
                  <h6 className="extra-small fw-bold text-uppercase text-primary mb-3">New Mapping</h6>

                  <label className="extra-small fw-bold text-muted mb-1">Local Attribute</label>
                  <select
                    className="form-select form-select-sm mb-2"
                    value={form.AttributeDefinitionId}
                    onChange={(e) => handleFieldChange("AttributeDefinitionId", e.target.value)}
                  >
                    <option value="">-- select --</option>
                    {definitions.map((d) => {
                      const id = GoldenSourceHelper.get(d, "AttributeDefinitionId");
                      return <option key={id} value={id}>{GoldenSourceHelper.get(d, "Label")}</option>;
                    })}
                  </select>

                  <label className="extra-small fw-bold text-muted mb-1">Taxonomy Version</label>
                  <select
                    className="form-select form-select-sm mb-2"
                    value={selectedHeaderId}
                    onChange={(e) => setSelectedHeaderId(e.target.value)}
                  >
                    <option value="">-- select --</option>
                    {headers.map((h, i) => {
                      const headerId = GoldenSourceHelper.get(h, "HeaderId");
                      return (
                        <option key={headerId || i} value={headerId}>
                          {GoldenSourceHelper.get(h, "VersionLabel")} ({GoldenSourceHelper.get(h, "TaxYear")})
                        </option>
                      );
                    })}
                  </select>

                  <label className="extra-small fw-bold text-muted mb-1">Group Taxonomy Concept</label>
                  <select
                    className="form-select form-select-sm mb-2"
                    value={form.TaxonomyDetailRowId}
                    disabled={!selectedHeaderId}
                    onChange={(e) => handleFieldChange("TaxonomyDetailRowId", e.target.value)}
                  >
                    <option value="">-- select --</option>
                    {concepts.map((c) => {
                      const rowId = GoldenSourceHelper.get(c, "RowId");
                      return (
                        <option key={rowId} value={rowId}>
                          {GoldenSourceHelper.get(c, "XbrlTag")} — {GoldenSourceHelper.get(c, "MeasureName")}
                        </option>
                      );
                    })}
                  </select>

                  <label className="extra-small fw-bold text-muted mb-1">Unit Conversion Factor</label>
                  <input
                    type="number"
                    step="any"
                    className="form-control form-control-sm mb-2"
                    value={form.UnitConversionFactor}
                    onChange={(e) => handleFieldChange("UnitConversionFactor", e.target.value)}
                  />

                  <label className="extra-small fw-bold text-muted mb-1">Aggregation Method</label>
                  <select
                    className="form-select form-select-sm mb-2"
                    value={form.AggregationMethodCode}
                    onChange={(e) => handleFieldChange("AggregationMethodCode", e.target.value)}
                  >
                    <option value="">-- select --</option>
                    {aggregationMethods.map((m) => {
                      const code = GoldenSourceHelper.get(m, "MethodCode");
                      return <option key={code} value={code}>{GoldenSourceHelper.get(m, "Label")}</option>;
                    })}
                  </select>

                  {requiresWeight && (
                    <>
                      <label className="extra-small fw-bold text-muted mb-1">Weight Attribute</label>
                      <select
                        className="form-select form-select-sm mb-2"
                        value={form.WeightAttributeDefinitionId}
                        onChange={(e) => handleFieldChange("WeightAttributeDefinitionId", e.target.value)}
                      >
                        <option value="">-- select --</option>
                        {definitions.map((d) => {
                          const id = GoldenSourceHelper.get(d, "AttributeDefinitionId");
                          return <option key={id} value={id}>{GoldenSourceHelper.get(d, "Label")}</option>;
                        })}
                      </select>
                      <p className="extra-small text-warning mb-2">
                        Weighted average is not yet implemented in the roll-up engine — this mapping will save but Calculate will reject it until v2.
                      </p>
                    </>
                  )}

                  <button
                    className="btn btn-sm btn-primary fw-bold mt-2"
                    disabled={saving || !form.AttributeDefinitionId || !form.TaxonomyDetailRowId || !form.AggregationMethodCode}
                    onClick={handleSave}
                  >
                    {saving ? "SAVING..." : "SAVE MAPPING"}
                  </button>
                </div>
              </div>

              {/* EXISTING MAPPINGS */}
              <div className="col-md-7">
                <div className="card shadow-sm border-0 bg-white overflow-hidden">
                  <div className="table-responsive" style={{ maxHeight: 560 }}>
                    <table className="table table-hover table-sm mb-0 align-middle">
                      <tbody>
                        {loading && (
                          <tr><td className="p-4 text-center text-muted extra-small italic">Loading...</td></tr>
                        )}
                        {!loading && mappings.length === 0 && (
                          <tr><td className="p-4 text-center text-muted extra-small italic">No mappings yet.</td></tr>
                        )}
                        {mappings.map((m, i) => {
                          const id = GoldenSourceHelper.get(m, "AttributeMappingId");
                          return (
                            <tr key={id || i}>
                              <td className="ps-3">
                                <div className="fw-bold extra-small">
                                  {GoldenSourceHelper.get(m, "AttributeLabel")} <i className="bi bi-arrow-right mx-1"></i> {GoldenSourceHelper.get(m, "XbrlTag")}
                                </div>
                                <div className="extra-small text-muted">
                                  &times;{GoldenSourceHelper.get(m, "UnitConversionFactor")} &middot; {GoldenSourceHelper.get(m, "AggregationMethodCode")}
                                </div>
                              </td>
                              <td className="text-end pe-3" style={{ width: 100 }}>
                                <button className="btn btn-sm btn-outline-danger fw-bold" onClick={() => handleRemove(id)}>
                                  REMOVE
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* FOOTER NAVIGATION */}
            <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
              <button
                className="btn btn-link text-decoration-none p-0 text-secondary small"
                onClick={() => navigate("/registered/site-admin")}
              >
                <i className="bi bi-arrow-left me-2"></i> Return to Site Administration
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
